/**
 * `search-aliases` and `search-aliases-mine` — FR-26 (alias mining half), NFR-13 · CLM-59.
 *
 * The **read side and the proposer** of the curated query vocabulary
 * (`clm.clause_search_alias`, 0023). The three writes — `create_search_alias`,
 * `approve_search_alias`, `set_search_alias_active` — stay where the ratified
 * `manage-clause` union already carries them (G23: the union is `.strict()` and
 * frozen); this file adds only what that union cannot express: a list with a
 * derived status and totals, and the mining action that *proposes* rows.
 *
 * ## Status is derived, never stored
 *
 * 0023 has `is_active`, `approved_by_upn`, `approved_utc` and no status column.
 * `suggested` = `NOT is_active AND approved_utc IS NULL`; `active` = `is_active`;
 * `inactive` = `NOT is_active AND approved_utc IS NOT NULL` (approved once, since
 * deactivated). `status=suggested` **is** the `suggested_aliases` queue that
 * `CLAUSE_REVIEW_QUEUES` (`manage-clause.ts`) reserved.
 *
 * ## The domain schema is extended, not edited
 *
 * `ClauseSearchAliasSchema` (`domain/clause.ts`) is ratified without the two
 * `approved_*` keys and the fixed `ManageClauseResponse.alias` keeps using it.
 * The list needs them — who approved and when is the evidence a reviewer reads —
 * so `SearchAliasSchema` extends the domain schema here.
 *
 * ## Corpus-level, both of them
 *
 * `clause_search_alias` and `clause_search_log` carry no party or contract
 * column, and FR-17 grants the library — and therefore its query vocabulary — to
 * every authorised user, so neither endpoint is scoped and `page.total_in_scope`
 * is `null` (the `clauses` argument).
 */
import { z } from 'zod';
import { IsoTimestampUtcSchema, UpnSchema } from '../primitives.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
import { AuditReceiptSchema } from '../provenance.js';
import { ClauseSearchAliasSchema } from '../domain/clause.js';
/* -------------------------------------------------------------------------- */
/* Shared                                                                      */
/* -------------------------------------------------------------------------- */
export const SEARCH_ALIAS_STATUSES = ['suggested', 'active', 'inactive'];
export const SearchAliasStatusSchema = z.enum(SEARCH_ALIAS_STATUSES);
/** The domain alias plus the approval evidence 0023 records with it. */
export const SearchAliasSchema = ClauseSearchAliasSchema.extend({
    approved_by_upn: UpnSchema.nullable(),
    approved_utc: IsoTimestampUtcSchema.nullable(),
    /** Derived server-side from `is_active` / `approved_utc`; never stored. */
    status: SearchAliasStatusSchema,
});
/* -------------------------------------------------------------------------- */
/* GET /v1/search/aliases                                                      */
/* -------------------------------------------------------------------------- */
export const SearchAliasListRequestSchema = z
    .object({
    /** Omitted lists all three statuses. `suggested` is the review queue. */
    status: SearchAliasStatusSchema.optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
export const SearchAliasListResponseSchema = z.object({
    items: z.array(SearchAliasSchema),
    page: PageInfoSchema,
    /** Unfiltered corpus counts — the three tiles never change with the filter. */
    totals: z.object({
        suggested: z.number().int().min(0),
        active: z.number().int().min(0),
        inactive: z.number().int().min(0),
    }),
});
/* -------------------------------------------------------------------------- */
/* POST /v1/search/aliases/mine                                                */
/* -------------------------------------------------------------------------- */
/**
 * Empty body, deliberately: the window, the occurrence floor, the similarity
 * threshold and the proposal cap come from the API's environment, so a click
 * cannot lower the bar.
 */
export const SearchAliasMineRequestSchema = z.object({}).strict();
export const SEARCH_ALIAS_SKIP_REASONS = ['exists', 'below_threshold', 'identity', 'too_few'];
export const SearchAliasSkipReasonSchema = z.enum(SEARCH_ALIAS_SKIP_REASONS);
export const SearchAliasMineResponseSchema = z.object({
    /** Every row is `origin 'Mined'`, `is_active false` — a proposal, never live. */
    proposed: z.array(SearchAliasSchema),
    skipped: z.array(z.object({
        alias_term: z.string().min(1).max(400),
        reason: SearchAliasSkipReasonSchema,
    })),
    window_days: z.number().int().min(1),
    min_occurrences: z.number().int().min(1),
    similarity_threshold: z.number().gt(0).max(1),
    /** One trigger receipt per proposed row (NFR-13). */
    audit: z.array(AuditReceiptSchema),
});
//# sourceMappingURL=search-aliases.js.map